import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, MapPin, FileText, Clock, CheckCircle, XCircle, Send,
  GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft:    { label: 'Draft',    icon: Clock,       color: 'text-[#6B7A73] bg-[#F0F3F1] border-[#E6EBE8]' },
  sent:     { label: 'Sent',     icon: Send,        color: 'text-[#2D6B68] bg-[#E5F0EE] border-[#C8D4CE]' },
  accepted: { label: 'Accepted', icon: CheckCircle, color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  rejected: { label: 'Rejected', icon: XCircle,     color: 'text-red-700 bg-red-50 border-red-200' },
};

function calcTotal(est) {
  let sub = 0;
  (est.sections || []).forEach(s => {
    (s.items || []).forEach(it => { sub += (it.qty || 0) * (it.unit_price || 0); });
  });
  return sub * (1 + (est.tax_gst || 5) / 100 + (est.tax_qst || 9.975) / 100);
}

export default function ClientDetail() {
  const { clientKey } = useParams();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientInfo, setClientInfo] = useState(null);

  useEffect(() => {
    if (!clientKey) return;
    setLoading(true);
    fetch(`${API}/clients/${encodeURIComponent(clientKey)}/estimates`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setEstimates(data);
        if (data.length > 0) setClientInfo(data[0].client || {});
      })
      .catch(() => toast.error('Could not load client'))
      .finally(() => setLoading(false));
  }, [clientKey]);

  const stats = useMemo(() => {
    const totalQuoted = estimates.reduce((s, e) => s + calcTotal(e), 0);
    const acceptedList = estimates.filter(e => (e.status || '').toLowerCase() === 'accepted');
    const totalAccepted = acceptedList.reduce((s, e) => s + calcTotal(e), 0);
    return {
      count: estimates.length,
      accepted: acceptedList.length,
      totalQuoted, totalAccepted,
    };
  }, [estimates]);

  const displayName = clientInfo?.company || clientInfo?.name || 'Client';
  const address = clientInfo
    ? [clientInfo.address1, clientInfo.address2, clientInfo.postal_code].filter(Boolean).join(', ')
    : '';

  return (
    <div className="animate-fade-in" data-testid="client-detail">
      <button
        onClick={() => navigate('/clients')}
        className="inline-flex items-center gap-1 text-sm text-[#6B7A73] hover:text-[#2D6B68] mb-4"
        data-testid="back-to-clients"
      >
        <ArrowLeft className="w-4 h-4" /> All clients
      </button>

      {/* Client header */}
      <div className="bg-white border border-[#E6EBE8] rounded-md p-6 mb-5">
        <h1 className="text-2xl font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
          {displayName}
        </h1>
        {clientInfo?.company && clientInfo?.name && (
          <p className="text-sm text-[#6B7A73] mt-1">{clientInfo.name}</p>
        )}
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          {clientInfo?.email && (
            <a href={`mailto:${clientInfo.email}`} className="flex items-center gap-1.5 text-[#3A423E] hover:text-[#2D6B68]">
              <Mail className="w-3.5 h-3.5 text-[#6B7A73]" /> {clientInfo.email}
            </a>
          )}
          {clientInfo?.phone && (
            <a href={`tel:${clientInfo.phone}`} className="flex items-center gap-1.5 text-[#3A423E] hover:text-[#2D6B68]">
              <Phone className="w-3.5 h-3.5 text-[#6B7A73]" /> {clientInfo.phone}
            </a>
          )}
          {address && (
            <span className="flex items-center gap-1.5 text-[#3A423E]">
              <MapPin className="w-3.5 h-3.5 text-[#6B7A73]" /> {address}
            </span>
          )}
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric label="Estimates" value={stats.count} />
          <Metric label="Accepted" value={stats.accepted} />
          <Metric label="Total quoted" value={`$${stats.totalQuoted.toLocaleString('en-US', { maximumFractionDigits: 0 })}`} />
          <Metric label="Total accepted" value={`$${stats.totalAccepted.toLocaleString('en-US', { maximumFractionDigits: 0 })}`} highlight={stats.totalAccepted > 0} />
        </div>
      </div>

      {/* Estimates list */}
      <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E6EBE8] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#2D6B68] uppercase tracking-[0.15em]">
            Estimates ({estimates.length})
          </h2>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : estimates.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B7A73]">No estimates for this client yet.</div>
        ) : (
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-[#F9F9F8] border-b border-[#E6EBE8] text-xs uppercase tracking-wider text-[#6B7A73]">
                <th className="text-left py-2.5 px-3 font-semibold">Job</th>
                <th className="text-left py-2.5 px-3 font-semibold">Type</th>
                <th className="text-left py-2.5 px-3 font-semibold">Estimate #</th>
                <th className="text-left py-2.5 px-3 font-semibold hidden md:table-cell">Created</th>
                <th className="text-right py-2.5 px-3 font-semibold">Total</th>
                <th className="text-center py-2.5 px-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6EBE8]">
              {estimates.map(est => {
                const cfg = STATUS_CONFIG[est.status] || STATUS_CONFIG.draft;
                const StatusIcon = cfg.icon;
                return (
                  <tr
                    key={est.estimate_id}
                    className="hover:bg-[#F9F9F8] cursor-pointer"
                    onClick={() => navigate(`/estimates/${est.estimate_id}`)}
                    data-testid={`client-est-${est.estimate_id}`}
                  >
                    <td className="py-3 px-3 max-w-[280px]">
                      <p className="text-sm text-[#1A1D1C] truncate" title={est.title}>{est.title || '—'}</p>
                    </td>
                    <td className="py-3 px-3">
                      {est.estimate_type && (
                        <span className="inline-block text-[11px] font-medium text-[#2D6B68] bg-[#E5F0EE] px-2 py-0.5 rounded">
                          {est.estimate_type.replace(/_/g, ' ')}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 text-xs text-[#3A423E]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {est.estimate_number || '—'}
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell text-xs text-[#6B7A73]">
                      {est.created_at ? new Date(est.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3 px-3 text-right text-sm text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      ${calcTotal(est).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${cfg.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {cfg.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div className={`bg-[#F9FAFA] border border-[#E6EBE8] rounded-md px-3 py-2.5 ${highlight ? 'border-[#2D6B68]' : ''}`}>
      <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7A73] font-semibold">{label}</p>
      <p className={`text-base font-semibold mt-0.5 ${highlight ? 'text-[#2D6B68]' : 'text-[#1A1D1C]'}`}
        style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
        {value}
      </p>
    </div>
  );
}
